<!-- frontend/src/components/LCA/Stepper/LCAReportViewer.vue -->
<template>
  <div class="step-container">
    <!-- Stepper Heading -->
    <StepperHeading :title="'Environmental Impact Report'" :step="4">
      <template #help>
        <p>Review the environmental impacts for the chosen solution scenario. This report provides detailed information about the potential environmental benefits of your selected approach.</p>
      </template>
    </StepperHeading>

    <!-- Loading State -->
    <div v-if="isLoadingImpacts" class="loading-container">
      <div class="spinner-container">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading environmental impact data...</span>
        </div>
      </div>
      <p>Loading environmental impact data...</p>
    </div>
    
    <div v-else>
      <!-- No solution selected -->
      <div v-if="!selectedSolution" class="empty-state">
        <div class="empty-state-icon">
          <i class="bi bi-clipboard-x"></i>
        </div>
        <h3>No Solution Selected</h3>
        <p>You need to select a solution in the previous steps before a report can be generated.</p>
      </div>
      
      <div v-else class="report-container">
        <!-- Display the ReuseHumanReport if the solution category is "reuse (human)" -->
        <ReuseHumanReport
          v-if="selectedSolution.solution_category?.toLowerCase() === 'reuse (human)'"
          ref="reportComponent"
          class="report-component"
        />
        
        <!-- Placeholder for unsupported solution categories -->
        <div v-else class="unsupported-card">
          <div class="card-header bg-warning">
            <h5 class="card-title">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              Not Implemented Yet
            </h5>
          </div>
          <div class="card-body">
            <p>
              The solution category
              "<strong>{{ selectedSolution.solution_category }}</strong>"
              is not yet supported for LCA reporting.
            </p>
            <p class="mt-3">
              We're working on expanding our LCA reporting capabilities to include more solution categories.
              Please check back later or try a different solution.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, nextTick, ref } from 'vue';
import StepperHeading from '@/components/Stepper/StepperHeading.vue';
import ReuseHumanReport from '../ReportTemplates/ReuseHuman.vue';
import { useRouter } from 'vue-router';
import { useLCAStore } from '@/stores/lca';
import { useStepperStore } from '@/stores/stepper';
import { usePageContextStore } from '@/stores/pageContext';

export default {
  name: 'LCAReportViewer',
  components: {
    StepperHeading,
    ReuseHumanReport,
  },
  async mounted() {
    const pageContextStore = usePageContextStore();
    const lcaStore = useLCAStore();
    const stepperStore = useStepperStore();
    
    console.log("Starting Environmental Impact Report initialization");

    // First check if we need to fetch product details
    if (!lcaStore.lcaData.productDetails && !lcaStore.productNotFound && stepperStore.wasteInfo.surplusName) {
      console.log("Product details missing, fetching for:", stepperStore.wasteInfo.surplusName);
      await lcaStore.fetchProductByName(stepperStore.wasteInfo.surplusName);
    }

    // Then proceed with environmental impacts if product details exist
    if (lcaStore.lcaData.productDetails?._id) {
      console.log("Fetching environmental impacts for product ID:", lcaStore.lcaData.productDetails._id);
      await lcaStore.fetchEnvironmentalImpacts(lcaStore.lcaData.productDetails._id);
    } else {
      console.warn("Cannot fetch environmental impacts - no product details available");
    }
    
    // Wait for the report component to render.
    await nextTick();
    let reportText = '';
    if (this.reportComponent && this.reportComponent.$el) {
      // Extract only the text content from the rendered report.
      reportText = this.reportComponent.$el.innerText;
    }
    // Set the page context with the full text of the generated report.
    pageContextStore.setPageContext(
      'Environmental Impact Report',
      'This page shows a specialized Environmental Impact Analysis for your chosen surplus and solution scenario.',
      reportText
    );
  },
  setup() {
    const router = useRouter();
    const lcaStore = useLCAStore();
    const stepperStore = useStepperStore();
    const pageContextStore = usePageContextStore();
    const reportComponent = ref(null);

    return {
      lcaStore,
      stepperStore,
      selectedSolution: lcaStore.lcaData.selectedSolution,
      isLoadingImpacts: lcaStore.isLoadingImpacts,
      reportComponent,
    };
  },
};
</script>

<style scoped>
.step-container {
  padding: 2rem;
  border-radius: 12px;
  background-color: white;
}

/* Loading state */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
}

.spinner-container {
  margin-bottom: 1rem;
}

.spinner-border.text-success {
  color: #87b140 !important;
}

/* Report container */
.report-container {
  margin-top: 1.5rem;
}

.report-component {
  animation: fadeIn 0.5s ease-in-out;
}

/* Unsupported category styling */
.unsupported-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-top: 1.5rem;
  transition: all 0.3s ease;
  border: 1px solid #ffeeba;
}

.unsupported-card:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.card-header {
  padding: 1.25rem 1.5rem;
}

.bg-warning {
  background-color: #fff3cd !important;
  color: #856404 !important;
}

.card-title {
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.card-body {
  padding: 1.5rem;
  color: #555;
  line-height: 1.6;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 2rem;
  margin-top: 1.5rem;
}

.empty-state-icon {
  background-color: #f2f5e7;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #87b140;
  margin-bottom: 1.5rem;
}

.empty-state h3 {
  color: #333;
  font-weight: 700;
  margin-bottom: 1rem;
}

.empty-state p {
  color: #666;
  max-width: 500px;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .step-container {
    padding: 1.5rem;
  }
  
  .card-header {
    padding: 1rem 1.25rem;
  }
  
  .card-body {
    padding: 1.25rem;
  }
  
  .empty-state-icon {
    width: 70px;
    height: 70px;
    font-size: 1.75rem;
  }
}

@media (max-width: 576px) {
  .step-container {
    padding: 1rem;
  }
  
  .card-title {
    font-size: 1rem;
  }
  
  .empty-state {
    padding: 2rem 1rem;
  }
  
  .empty-state-icon {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
  
  .empty-state h3 {
    font-size: 1.2rem;
  }
  
  .empty-state p {
    font-size: 0.9rem;
  }
}
</style>





